import { Component, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { isChunkLoadError, reloadOnceForNewDeploy } from '@/lib/chunkReload';

/**
 * Last line of defence: without it any render error unmounts the whole app and the visitor
 * sees an empty page (the 2026-10-27 church-map blank). Stale chunks reload once; anything
 * else shows a reload screen and is reported, so the error is seen instead of silent.
 */
export default class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) { return { error }; }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && reloadOnceForNewDeploy()) return;
    try { (posthog as unknown as { captureException?: (e: unknown, p?: object) => void }).captureException?.(error, { boundary: 'app', path: window.location.pathname }); } catch { /* analytics off */ }
    reportClientError(error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <img src="/mystudyfund-logo.png" alt="MyStudyFund" className="h-9 w-auto" />
        <h1 className="text-2xl font-bold text-foreground">This page didn’t load properly</h1>
        <p className="max-w-md text-foreground/70">We’ve been notified. Reloading usually fixes it.</p>
        <button type="button" onClick={() => window.location.reload()} className="rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground">Reload the page</button>
      </div>
    );
  }
}

/** Fire-and-forget beacon to the Control Center, which turns a burst of these into an alert. */
export function reportClientError(error: unknown) {
  try {
    const body = JSON.stringify({ product: 'mystudyfund', path: window.location.pathname, message: String((error as Error)?.message ?? error).slice(0, 300), ua: navigator.userAgent.slice(0, 160) });
    navigator.sendBeacon?.('https://cc.concept.zone/api/client-error', new Blob([body], { type: 'text/plain' }));
  } catch { /* never let reporting break the page */ }
}
